import { ProjectItem } from "@/store/project/projects-service"
import { useTranslation } from "react-i18next"
import i18n from "i18next"
import { getProductName } from "@/utils/product-name"
import { Icon } from "@/components/icons/Icon"
import { replaceDialog } from "@/store/dialogs-state"
import { ConfirmDialog } from "./Dialogs/ConfirmDialog"
import { showContextMenu } from "@/components/ContextMenuManager"
import { ContextMenuItem, ContextMenuSeparator } from "@/components/ui/ContextMenu"
import { analytics } from "@/telemetry/amplitude"
import { DataSource } from "@/store/data-source"
import { ImageFormat, processImageUrl } from "@/utils/process-image-url"
import { getAvailablePreview } from "@/store/previews/previews-state"
import { Image } from "./ui/Image"

import {
	showChangeProjectDateDialog,
	showRenameProjectDialog,
} from "@/store/project/update-project"
import { DeleteProjectDialog } from "./Dialogs/ProjectDeleteDialog"
import { PreviewSize } from "@/store/previews/previews-types"
import clsx from "clsx"
import { useCallback, useRef, useState } from "react"
import { routes } from "@/utils/routes"
import { useBrowserCapabilities } from "@/store/app/browser-capabilities"
import { SwitchToChromeDialog } from "@/components/Dialogs/SwitchToChromeDialog"
import { preflightProjectOpen } from "@/store/project/open-project-preflight"
import { handleProjectOpenPreflightResult } from "@/store/project/open-project-preflight-ui"

interface ProjectThumbnailProps {
	displayInfo?: boolean
	project: ProjectItem
}

const useProjectOpenHandler = (project: ProjectItem) => {
	const navigateToProject = routes.project.useNavigate()
	const capabilities = useBrowserCapabilities()
	const isOpeningProjectRef = useRef(false)

	return useCallback(
		(e: React.MouseEvent) => {
			if (e.button !== 0 || e.ctrlKey || e.metaKey) {
				return
			}
			if (isOpeningProjectRef.current) {
				return
			}
			isOpeningProjectRef.current = true

			analytics.trackEvent(
				{
					label: "open_project",
					type: "click",
					customProperties: { projectId: project.id },
				},
				e.target,
			)

			// In sample-only browsers, block opening non-sample projects from the
			// home screen and instead show the "open in chrome or download the app"
			// modal. This mirrors the session-level gating and ensures we don't even
			// navigate into the project route for blocked projects.
			if (capabilities.supportsSampleOnly && !project.isSampleProject) {
				replaceDialog(SwitchToChromeDialog)
				isOpeningProjectRef.current = false
				return
			}

			void preflightProjectOpen(project)
				.then((result) => {
					if (handleProjectOpenPreflightResult(result, { project })) {
						return navigateToProject({ projectId: project.id })
					}
				})
				.catch((error) => {
					handleProjectOpenPreflightResult(
						{ ok: false, reason: "sample_mount_error", error },
						{ project },
					)
				})
				.finally(() => {
					isOpeningProjectRef.current = false
				})
		},
		[capabilities.supportsSampleOnly, navigateToProject, project],
	)
}

const handleProjectContextMenu = (e: React.MouseEvent, project: ProjectItem) => {
	e.stopPropagation()

	analytics.trackEvent(
		{ label: "open_project_menu", type: "click", value: project?.id },
		e.target,
	)

	showContextMenu(
		e,
		<div aria-label={i18n.t("projectThumbnail.ariaProjectThumbnailMenu", { ns: "components" })}>
			<ContextMenuItem
				trackingLabel="rename"
				disabled={project.isSampleProject}
				onClick={() => showRenameProjectDialog(project)}
			>
				{i18n.t("projectThumbnail.rename", { ns: "components" })}
			</ContextMenuItem>
			<ContextMenuItem
				trackingLabel="change_date"
				onClick={() => showChangeProjectDateDialog(project)}
			>
				{i18n.t("projectThumbnail.editProjectDate", { ns: "components" })}
			</ContextMenuItem>
			<ContextMenuSeparator />
			<ContextMenuItem
				trackingLabel="delete"
				onClick={() => replaceDialog(DeleteProjectDialog, { project })}
			>
				{i18n.t("projectThumbnail.deleteProject", { ns: "components" })}
			</ContextMenuItem>
		</div>,
	)
}
export const ProjectThumbnail: React.FC<ProjectThumbnailProps> = ({ displayInfo, project }) => {
	const { t } = useTranslation("components")
	const handleProjectClick = useProjectOpenHandler(project)
	const displayName =
		project.isSampleProject || project.name === "Sample Project"
			? t("projectThumbnail.sampleProject")
			: project.name

	const showMigrateProjectDialog: React.MouseEventHandler = (evt) => {
		evt.stopPropagation()
		project &&
			replaceDialog(ConfirmDialog, {
				title: t("projectThumbnail.catalogChangeTitle", { productName: getProductName() }),
				message: t("projectThumbnail.catalogChangeMessage", {
					productName: getProductName(),
				}),
				primary: { title: t("common:action.confirm") },
				trackingViewName: "project_migration_prompt",
			})
	}

	const renderPreview = (index: number) => {
		const photoId = project?.previewThumbnailPhotoIds?.[index]
		const editedPreview = photoId
			? getAvailablePreview({
					photoId,
					projectId: project.id,
					size: PreviewSize.medium,
				})
			: undefined

		const previewUrl = editedPreview?.url ?? project?.previewThumbnailUrls?.[index]
		return (
			<div
				key={index}
				className="flex aspect-[4/3] items-center justify-center bg-base-700/50 object-cover"
			>
				{previewUrl && (
					<Image
						animateWhileLoading
						className="aspect-[4/3] size-full object-cover"
						key={index}
						src={processImageUrl({ url: previewUrl, format: ImageFormat.AVIF })}
					/>
				)}
				{!previewUrl && (
					<Icon
						name="no_photo"
						size={32}
						className="text-base-500"
						aria-label={t("projectThumbnail.ariaNoPreviewAvailable")}
					/>
				)}
			</div>
		)
	}

	return (
		<div
			className="menu-root relative flex h-fit flex-col space-y-4 overflow-hidden rounded-lg bg-base-700 hover:bg-base-600 data-[selected]:bg-base-600"
			onClick={handleProjectClick}
			onContextMenu={(e) => handleProjectContextMenu(e, project)}
			role="group"
			aria-label={t("projectThumbnail.ariaProjectEntry")}
		>
			<div className="bg-base-800 object-cover">{renderPreview(0)}</div>
			{displayInfo && (
				<div className="flex flex-col space-y-2 px-4 pb-4">
					<div className="flex flex-row items-center justify-between space-x-4">
						<div className="grow overflow-hidden truncate text-sm font-semibold">
							{displayName}
						</div>
						<button
							aria-label={t("projectThumbnail.ariaProjectOptions", {
								name: displayName,
							})}
							className="shrink-0 rounded-sm text-base-300 hover:text-base-50"
							onClick={(e) => handleProjectContextMenu(e, project)}
						>
							<Icon name="more" />
						</button>
					</div>

					<div className="flex flex-row justify-between space-x-2">
						<div className="overflow-hidden truncate text-xs text-base-400">
							{`${project.eventStartedAt?.toLocaleDateString(i18n.language, {
								month: "short",
								day: "numeric",
								year: "numeric",
							})}`}
						</div>
						{project.photoCount != null && (
							<div className="overflow-hidden truncate text-xs text-base-400">
								{t("projectThumbnail.photoCount", {
									count: project.photoCount ?? 0,
								})}
							</div>
						)}
						{project.source == DataSource.IndexedDB && (
							<Icon
								className="rounded-sm hover:bg-base-600"
								name="error"
								size={14}
								onClick={showMigrateProjectDialog}
							/>
						)}
					</div>
				</div>
			)}
			<button
				aria-label={t("projectThumbnail.ariaOpenProject", { name: displayName })}
				className="sr-only"
			/>
		</div>
	)
}

export const ProjectListItemView = (props: { project: ProjectItem; layout: string[] }) => {
	const { t } = useTranslation("components")
	const { project, layout } = props
	const handleProjectClick = useProjectOpenHandler(project)
	const displayName =
		project.isSampleProject || project.name === "Sample Project"
			? t("projectThumbnail.sampleProject")
			: project.name
	const coverPhotoId = project.previewThumbnailPhotoIds?.[0]
	const editedPreview = coverPhotoId
		? getAvailablePreview({
				photoId: coverPhotoId,
				projectId: project.id,
				size: PreviewSize.medium,
			})
		: undefined

	const previewUrl = editedPreview?.url ?? project?.previewThumbnailUrls?.[0]

	const [isHovering, setIsHovering] = useState(false)

	return (
		<div
			className="relative flex h-fit flex-row items-center space-x-4 overflow-hidden rounded-lg bg-base-800 hover:bg-base-700 data-[selected]:bg-base-700"
			data-project-id={project.id}
			onClick={handleProjectClick}
			onContextMenu={(e) => handleProjectContextMenu(e, project)}
			onMouseEnter={() => setIsHovering(true)}
			onMouseLeave={() => setIsHovering(false)}
			role="group"
			aria-label={t("projectThumbnail.ariaProjectEntry")}
		>
			{previewUrl ? (
				<Image
					size={10}
					animateWhileLoading
					className={clsx(layout[0], "aspect-square object-cover")}
					src={processImageUrl({ url: previewUrl, format: ImageFormat.AVIF })}
				/>
			) : (
				<div className={clsx(layout[0], "aspect-square bg-base-700")}></div>
			)}

			<div className={clsx(layout[1], "overflow-hidden truncate text-sm font-semibold")}>
				{displayName}
			</div>
			<div className={clsx(layout[2], "text-xs text-base-400")}>
				{`${project.eventStartedAt?.toLocaleDateString(i18n.language, {
					month: "short",
					day: "numeric",
					year: "numeric",
				})}`}
			</div>

			<div
				className={clsx(
					layout[3],
					"flex flex-row items-center justify-between text-xs text-base-400",
				)}
			>
				{t("projectThumbnail.photoCount", { count: project.photoCount ?? 0 })}
				<button
					aria-label={t("projectThumbnail.ariaProjectOptions", { name: displayName })}
					className={clsx(
						"mx-2 shrink-0 rounded-sm text-base-300 hover:text-base-50",
						!isHovering && "opacity-0",
					)}
					onClick={(e) => handleProjectContextMenu(e, project)}
				>
					<Icon name="more" />
				</button>
			</div>
			<button
				aria-label={t("projectThumbnail.ariaOpenProject", { name: displayName })}
				className="sr-only"
			/>
		</div>
	)
}
